import React from 'react';
import { FiAlertTriangle } from 'react-icons/fi';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { BaseExportModal } from './BaseExportModal';
import { EXPORT_COLORS } from '../../constants';
import type { ExportDateRange, QuotaModalState } from '../../types';
import type { RootState } from '../../../../state/store';
import { IoDownload } from 'react-icons/io5';
import ExportDateRangePicker from '../exportDateRangePicker';

interface ExportModalProps {
  quotaModal: QuotaModalState;
  exportDateRange: ExportDateRange | null;
  onExportDateRangeChange: (range: ExportDateRange) => void;
  onClose: () => void;
  onProceed: () => void;
  onDownloadExisting?: (downloadUrl: string) => void;
  onViewSavedReports?: () => void;
}

// Enum for different modal states
enum ModalState {
  LOADING = 'loading',
  EXISTING_REPORT = 'existing_report',
  PENDING_REPORT = 'pending_report',
  OVERLAPPING_REPORTS = 'overlapping_reports',
  NEW_EXPORT = 'new_export',
  EXPORT_PROCESSING = 'export_processing',
  EXPORT_COMPLETED = 'export_completed'
}

export const ExportModal: React.FC<ExportModalProps> = ({
  quotaModal,
  exportDateRange,
  onExportDateRangeChange,
  onClose,
  onProceed,
  onDownloadExisting = () => {},
  onViewSavedReports = () => {}
}) => {
  const theme = useSelector((state: RootState) => state.theme.mode);
  const isDark = theme !== 'light';

  // Determine the current modal state
  const getModalState = (): ModalState => {
    // Check for export processing/completed states first
    if (quotaModal.exportCompleted) return ModalState.EXPORT_COMPLETED;
    if (quotaModal.exportProcessing) return ModalState.EXPORT_PROCESSING;

    if (quotaModal.loading) return ModalState.LOADING;
    if (!quotaModal.data) return ModalState.LOADING; // Fallback to loading if no data
    if (quotaModal.data.existing_report_available) return ModalState.EXISTING_REPORT;
    if (quotaModal.data.pending_report_available) return ModalState.PENDING_REPORT;
    if (quotaModal.data.overlapping_reports_available && quotaModal.data.overlapping_reports) {
      return ModalState.OVERLAPPING_REPORTS;
    }
    return ModalState.NEW_EXPORT;
  };

  const modalState = getModalState();

  // Handle download functionality
  const handleDownloadExisting = () => {
    if (quotaModal.data?.download_url) {
      const link = document.createElement('a');
      link.href = quotaModal.data.download_url;
      link.download = '';
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      onDownloadExisting(quotaModal.data.download_url);
      onClose();
    }
  };

  const handleDownloadReport = (downloadUrl: string) => {
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = '';
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Render different footer content based on state
  const renderFooter = () => {
    switch (modalState) {
      case ModalState.LOADING:
      case ModalState.PENDING_REPORT:
        return null;

      case ModalState.EXISTING_REPORT:
        return (
          <>
            <button className="btn btn-success" onClick={handleDownloadExisting}>
              Download Report
            </button>
          </>
        );

      case ModalState.EXPORT_PROCESSING:
        return (
          <FooterContainer>
            <button className="btn btn-primary" disabled>
              Processing...
            </button>
            <HelpText>
              You can close this dialog, you will receive a notification when export is ready.
            </HelpText>
          </FooterContainer>
        );

      case ModalState.EXPORT_COMPLETED:
        return (
          <>
            <button className="btn btn-success" onClick={onViewSavedReports}>
              View Saved Reports
            </button>
          </>
        );

      case ModalState.OVERLAPPING_REPORTS:
      case ModalState.NEW_EXPORT:
        return (
          <>
            <button className="btn btn-primary" onClick={onProceed}>
              {modalState === ModalState.OVERLAPPING_REPORTS
                ? 'Proceed with New Export'
                : 'Start Export'}
            </button>
          </>
        );

      default:
        return null;
    }
  };

  const renderExportDateRangePicker = () => {
    if (!exportDateRange) {
      return null;
    }

    if (
      modalState !== ModalState.NEW_EXPORT &&
      modalState !== ModalState.OVERLAPPING_REPORTS
    ) {
      return null;
    }

    return (
      <ExportDateRangePicker
        value={exportDateRange}
        onChange={onExportDateRangeChange}
      />
    );
  };

  // Render main content section based on state
  const renderMainContent = () => {
    const reportType = quotaModal.reportType;
    const quotaData = quotaModal.data;

    switch (modalState) {
      case ModalState.EXPORT_PROCESSING:
        return (
          <ProgressSection>
            <ProgressContent>
              <CustomSpinner />
              <ProgressText>
                <h4>Export In Progress</h4>
                <p>You&apos;ll receive an email once the export is complete.</p>
              </ProgressText>
            </ProgressContent>
          </ProgressSection>
        );

      case ModalState.EXPORT_COMPLETED:
        return (
          <ReadySection $isDark={isDark}>
            <ReadyContent $isDark={isDark}>
              <IoDownload className="download-icon" />
              <ReadyText $isDark={isDark}>
                <h4>Your Export is Ready</h4>
                <p>
                  The <strong>{reportType?.name}</strong> report has been generated and is ready to
                  download. We have also sent an email with this file.
                </p>
              </ReadyText>
            </ReadyContent>
          </ReadySection>
        );

      case ModalState.LOADING:
        return (
          <ProgressSection>
            <ProgressContent>
              <CustomSpinner />
              <ProgressText>
                <h4>Checking Export Status</h4>
                <p>Please wait while we check the export quota and availability...</p>
              </ProgressText>
            </ProgressContent>
          </ProgressSection>
        );

      case ModalState.EXISTING_REPORT:
        return (
          <ReadySection $isDark={isDark}>
            <ReadyContent $isDark={isDark}>
              <IoDownload className="download-icon" />
              <ReadyText $isDark={isDark}>
                <h4>Your Report is Ready</h4>
                <p>A copy of the file has also been sent to your email.</p>
              </ReadyText>
            </ReadyContent>
          </ReadySection>
        );

      case ModalState.PENDING_REPORT:
        return (
          <ProgressSection>
            <ProgressContent>
              <CustomSpinner />
              <ProgressText>
                <h4>Export In Progress</h4>
                <p>You&apos;ll receive an email once the export is complete.</p>
              </ProgressText>
            </ProgressContent>
          </ProgressSection>
        );

      case ModalState.OVERLAPPING_REPORTS:
        return (
          <>
            {renderExportDateRangePicker()}
            <WarningSection>
            <FiAlertTriangle className="warning-icon" />
            <WarningText>
              <h4>Overlapping Reports Found</h4>
              <p>The following reports have overlapping date ranges with your selection:</p>
              <ReportsList>
                {quotaData?.overlapping_reports?.map((report, index) => {
                  const startDate = new Date(report.time_range.start_date * 1000);
                  const endDate = new Date(report.time_range.end_date * 1000);
                  return (
                    <OverlappingReportItem key={report.id}>
                      <div className="report-info">
                        <strong>Report #{index + 1}</strong>
                        {report.time_range && (
                          <div className="date-range">
                            {startDate.toLocaleDateString()} - {endDate.toLocaleDateString()}
                          </div>
                        )}
                        <div className="report-status">
                          Status: {report.status} | Created:{' '}
                          {new Date(report.created_at * 1000).toLocaleDateString()}
                        </div>
                      </div>
                      <DownloadButton
                        className="btn btn-outline-success btn-sm"
                        onClick={() => handleDownloadReport(report.download_url)}
                      >
                        Download
                      </DownloadButton>
                    </OverlappingReportItem>
                  );
                })}
              </ReportsList>
              <OverlappingNote>
                <strong>Note:</strong> You can proceed with the new export or download the
                overlapping reports above.
              </OverlappingNote>
            </WarningText>
          </WarningSection>
          </>
        );

      case ModalState.NEW_EXPORT:
        return renderExportDateRangePicker();

      default:
        return null;
    }
  };

  // Lite plan export limits are shown in the date range picker notice.
  const renderQuotaSection = () => {
    return null;
  };

  // Render what's included section
  const renderWhatsIncludedSection = () => {
    if (modalState === ModalState.NEW_EXPORT) {
      return (
        <FixedSection $isDark={isDark}>
          <FixedSectionHeader $isDark={isDark}>
            <span>What to expect</span>
          </FixedSectionHeader>
          <SectionContent $isDark={isDark}>
            <ul>
              <li>Processing time varies by selected range</li>
              <li>Report will be available in Saved Reports for 7 days</li>
              <li>You'll receive an email when it's ready</li>
            </ul>
          </SectionContent>
        </FixedSection>
      );
    }

    // For other states, show generic what's included
    return null;
  };

  if (!quotaModal.show) {
    return null;
  }

  const showExportDatePicker =
    Boolean(exportDateRange) &&
    (modalState === ModalState.NEW_EXPORT || modalState === ModalState.OVERLAPPING_REPORTS);

  return (
    <BaseExportModal
      show={quotaModal.show}
      onClose={onClose}
      title="Export"
      footer={renderFooter()}
      showTwoColumns={false}
      wide={showExportDatePicker}
    >
      <ModalContainer $isDark={isDark}>
        <ReportTitle $isDark={isDark}>{quotaModal.reportType?.name || 'Export Report'}</ReportTitle>

        {renderMainContent()}

        {renderQuotaSection()}
        {renderWhatsIncludedSection()}
      </ModalContainer>
    </BaseExportModal>
  );
};

// Styled Components
const ModalContainer = styled.div<{ $isDark: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 20px;
  color: ${({ $isDark }) => ($isDark ? '#f9fafb' : '#374151')};
`;

const ReportTitle = styled.h3<{ $isDark: boolean }>`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: ${({ $isDark }) => ($isDark ? '#f9fafb' : '#374151')};
  text-align: left;
`;

const CustomSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 4px solid rgba(16, 185, 129, 0.2);
  border-left: 4px solid #10b981;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  flex-shrink: 0;

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;

const ProgressSection = styled.div`
  background: #5a6c7d;
  border-radius: 8px;
  padding: 40px 24px;
  margin: 16px 0;
`;

const ProgressContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 24px;
`;

const ProgressText = styled.div`
  h4 {
    margin: 0 0 12px 0;
    font-size: 18px;
    font-weight: 600;
    color: white;
  }

  p {
    margin: 0 0 12px 0;
    font-size: 14px;
    color: #e5e7eb;
    line-height: 1.5;
  }
`;

const ReadySection = styled.div<{ $isDark: boolean }>`
  background: ${({ $isDark }) => ($isDark ? '#2E2E33' : '#f3f4f6')};
  border-radius: 8px;
  padding: 40px 24px;
  margin: 16px 0;
`;

const ReadyContent = styled.div<{ $isDark: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 24px;

  .download-icon {
    width: 32px;
    height: 32px;
    color: ${({ $isDark }) => ($isDark ? '#9ca3af' : '#6b7280')};
    flex-shrink: 0;
  }
`;

const ReadyText = styled.div<{ $isDark: boolean }>`
  h4 {
    margin: 0 0 12px 0;
    font-size: 18px;
    font-weight: 600;
    color: ${({ $isDark }) => ($isDark ? '#f9fafb' : '#514f4f')};
  }

  p {
    margin: 0;
    font-size: 14px;
    color: ${({ $isDark }) => ($isDark ? '#d1d5db' : '#514f4f')};
    line-height: 1.5;
  }

  strong {
    font-weight: 600;
  }
`;

const WarningSection = styled.div`
  background: #fbbf24;
  border-radius: 8px;
  padding: 24px;
  display: flex;
  align-items: flex-start;
  gap: 16px;
  margin: 16px 0;

  .warning-icon {
    font-size: 32px;
    color: white;
    flex-shrink: 0;
    margin-top: 4px;
  }
`;

const WarningText = styled.div`
  h4 {
    margin: 0 0 8px 0;
    font-size: 16px;
    font-weight: 600;
    color: white;
  }

  p {
    margin: 0 0 16px 0;
    font-size: 14px;
    color: #f3f4f6;
    line-height: 1.4;
  }
`;

const ReportsList = styled.div`
  margin: 16px 0;
`;

const OverlappingReportItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 6px;
  padding: 12px 16px;
  margin-bottom: 12px;

  .report-info {
    flex: 1;

    strong {
      color: white;
      display: block;
      margin-bottom: 4px;
    }

    .date-range {
      color: #f3f4f6;
      font-size: 13px;
      margin-bottom: 2px;
    }

    .report-status {
      color: #f3f4f6;
      font-size: 12px;
    }
  }
`;

const DownloadButton = styled.button`
  padding: 8px 12px;
  border-radius: 6px;
  font-weight: 500;
  transition: all 0.2s ease;
  border: 1px solid white;
  background: transparent;
  color: white;
  cursor: pointer;
  font-size: 12px;

  &:hover:not(:disabled) {
    background: white;
    color: #fbbf24;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const OverlappingNote = styled.div`
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 6px;
  padding: 12px;
  margin-top: 12px;
  font-size: 13px;
  color: #f3f4f6;

  strong {
    color: white;
  }
`;

const FixedSection = styled.div<{ $isDark: boolean }>`
  border: 1px solid ${({ $isDark }) => ($isDark ? '#4b5563' : '#e5e7eb')};
  border-radius: 6px;
  overflow: hidden;
`;

const FixedSectionHeader = styled.div<{ $isDark: boolean }>`
  padding: 12px 16px;
  background: ${({ $isDark }) => ($isDark ? '#2E2E33' : '#f9fafb')};
  border: none;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: 500;
  color: ${({ $isDark }) => ($isDark ? '#f9fafb' : ({ theme }) => theme.text_color)};

  span {
    font-size: 14px;
  }
`;

const SectionContent = styled.div<{ $isDark: boolean }>`
  padding: 16px;
  background: ${({ $isDark }) => ($isDark ? '#2E2E33' : 'white')};
  border-top: 1px solid ${({ $isDark }) => ($isDark ? '#4b5563' : '#e5e7eb')};

  p {
    margin: 0;
    font-size: 14px;
    color: ${({ $isDark }) => ($isDark ? '#d1d5db' : '#6b7280')};
    line-height: 1.5;
  }

  ul {
    margin: 0 0 16px 0;
    padding-left: 20px;
    color: ${({ $isDark }) => ($isDark ? '#d1d5db' : EXPORT_COLORS.secondary)};
    font-size: 13px;

    li {
      margin-bottom: 4px;
    }
  }
`;

const FooterContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`;

const HelpText = styled.p`
  margin: 0;
  font-size: 13px;
  color: ${({ theme }) => theme.text_color || '#6b7280'};
  text-align: center;
  line-height: 1.4;
  opacity: 0.8;
`;
